import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { api } from "@/lib/api";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import VideoCard from "@/components/plyx/VideoCard";
import { Loader2, X, Send, Plus, Sparkles, Coins, Crown, User, Compass } from "lucide-react";
import { toast } from "sonner";
import { isPlyxStandaloneMode, isPlyxProductionDomain } from "@/lib/plyxStandalone";
import PlyxExitModal from "@/components/plyx/PlyxExitModal";
import MakerFeedCard from "@/components/plyx/MakerFeedCard";
import MillionsFeedCard from "@/components/plyx/MillionsFeedCard";
import DailyBonusCard from "@/components/plyx/DailyBonusCard";
import { GuardedText } from "@/components/GuardedText";
import CommentDrawer from "@/components/plyx/CommentDrawer";
import { PLYX_BUILD } from "@/lib/buildInfo";

// Drop a maker spotlight into the swipeable feed every Nth slide.
const MAKER_INTERLEAVE_EVERY = 9;
// Drop a Plyx Millions promo card into the feed every Nth slide.
const MILLIONS_INTERLEAVE_EVERY = 20;

const StandaloneExitChip = () => {
  const [open, setOpen] = useState(false);
  // Only show the "Preview · Exit" chip in the preview environment (e.g.
  // preview.emergentagent.com). On the real Plyx domain (plyx.co.uk) we hide
  // it so visitors don't see a preview tag on the live product.
  if (!isPlyxStandaloneMode() || isPlyxProductionDomain()) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="plyx-feed-exit-preview"
        title="Exit standalone Plyx preview"
        className="fixed top-3 left-3 z-40 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-semibold text-white/85 border border-[#00E0C7]/40 bg-black/60 backdrop-blur hover:bg-white/10/15 hover:text-white transition-colors cursor-pointer"
      >
        <span>Preview</span>
        <span className="opacity-50">·</span>
        <span className="text-white/80">Exit</span>
      </button>
      <PlyxExitModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

const PlyxFeed = () => {
  const [videos, setVideos] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter-122: initial feed fetch failed (timeout / network drop) —
  // drives the "Tap to retry" screen instead of an infinite spinner
  // or the misleading "no videos yet" empty state.
  const [loadError, setLoadError] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [commentVideo, setCommentVideo] = useState(null);
  const [tab, setTab] = useState(() => localStorage.getItem("plyx_feed_tab") || "foryou");
  const [channel, setChannel] = useState(() => localStorage.getItem("plyx_feed_channel") || "");
  const [channels, setChannels] = useState([]);
  const [makerPool, setMakerPool] = useState([]);
  // iter-119: ref (not state) so toggling doesn't bust the loadMore useCallback
  // identity and re-trigger the tab effect → infinite loop.
  const didFallbackToForYouRef = useRef(false);
  // iter-119: monotonic counter — each loadMore call grabs the current value
  // and only commits its setState if its id is still the latest. Cleanly
  // handles React StrictMode's double-mount AND user-driven tab swaps that
  // interleave async requests.
  const loadReqIdRef = useRef(0);
  const containerRef = useRef(null);
  const { user } = usePlyxAuth();

  const loadMore = useCallback(async (currentCursor, currentTab, currentChannel) => {
    const myReqId = ++loadReqIdRef.current;
    const isLatest = () => loadReqIdRef.current === myReqId;
    try {
      setLoading(true);
      setLoadError(false);
      const params = new URLSearchParams();
      params.set("tab", currentTab);
      if (currentCursor) params.set("cursor", currentCursor);
      if (currentChannel) params.set("channel", currentChannel);
      // iter-122: hard 15s timeout. Axios has NO default timeout, so a
      // stalled connection (page navigation killing sockets, mobile
      // network flaps) left the request pending FOREVER — the exact
      // "Loading Plyx… for 30 minutes" hang. Now it fails fast into
      // the retry screen instead.
      const { data } = await plyxApi.get(`/plyx/feed/v2?${params.toString()}`, { timeout: 15000 });
      if (!isLatest()) return;
      const items = data.items || [];
      // iter-119: Following tab returns 0 items when the viewer is
      // signed-out OR follows no-one who posted recently. Fall back to
      // For You so the Home tab always has something to show.
      if (
        !currentCursor &&
        items.length === 0 &&
        currentTab === "following" &&
        !didFallbackToForYouRef.current
      ) {
        didFallbackToForYouRef.current = true;
        const fbParams = new URLSearchParams();
        fbParams.set("tab", "foryou");
        if (currentChannel) fbParams.set("channel", currentChannel);
        const fb = await plyxApi.get(`/plyx/feed/v2?${fbParams.toString()}`, { timeout: 15000 });
        if (!isLatest()) return;
        setVideos(fb.data?.items || []);
        setCursor(fb.data?.next_cursor || null);
        return;
      }
      // iter-120: a channel chip that yields ZERO clips used to strand
      // the viewer on the full-screen empty state (which hides the
      // tabs + chips, so there was no way back). Auto-recover to the
      // "All" feed instead — the tab/channel effect refires loadMore.
      if (!currentCursor && items.length === 0 && currentChannel && currentTab !== "following") {
        setChannel("");
        return;
      }
      setVideos((prev) => {
        if (!currentCursor) return items;
        // iter-120: STRICT full-array dedup — a clip can never appear
        // twice until the viewer has scrolled the entire catalogue.
        // Only when a whole page comes back 100% already-seen (the
        // backend seq wrapped) do we let the loop restart so swiping
        // never dead-ends.
        const seen = new Set(prev.map((v) => v.video_id));
        const fresh = items.filter((v) => !seen.has(v.video_id));
        return fresh.length > 0 ? [...prev, ...fresh] : [...prev, ...items];
      });
      setCursor(data.next_cursor);
    } catch (e) {
      if (isLatest()) {
        // iter-122: only the INITIAL page failure blanks the feed —
        // flag it so the retry screen renders. Mid-scroll pagination
        // failures keep the existing slides and just toast.
        if (!currentCursor) setLoadError(true);
        toast.error("Could not load feed");
      }
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, []);

  // Load the editorial channel chips once on mount. Empty channels
  // (count===0) are dropped so we never offer a chip that opens an
  // empty feed.
  useEffect(() => {
    plyxApi.get("/plyx/channels")
      .then((r) => setChannels((r.data?.channels || []).filter((c) => c.count > 0)))
      .catch(() => { /* chips are optional UI */ });
  }, []);

  // Golden Hour status — makes the Millions pill glow while the nightly
  // tournament is live (also lazily settles the previous window server-side).
  const [goldenActive, setGoldenActive] = useState(false);
  useEffect(() => {
    plyxApi.get("/plyx/millions/golden")
      .then((r) => setGoldenActive(!!r.data?.active))
      .catch(() => {});
  }, []);

  // Fetch a small pool of featured makers once on mount. We rotate
  // through them as the user scrolls so the same maker isn't seen twice
  // in the same session.
  useEffect(() => {
    api.get("/makers/showcases?limit=12&sort=featured")
      .then((r) => setMakerPool(r.data?.items || []))
      .catch(() => { /* feed still works without makers */ });
  }, []);

  // Stable interleaved slide list: every Nth slot becomes a maker card.
  // This keeps the same set of indices stable across re-renders so the
  // IntersectionObserver doesn't churn.
  const slides = useMemo(() => {
    if (videos.length === 0) return [];
    const out = [];
    let videoCursor = 0;
    let makerCursor = 0;
    let slideIdx = 0;
    while (videoCursor < videos.length) {
      // Every ~20 slides a Plyx Millions promo card invites the viewer
      // into the trivia game; makers keep their every-9th cadence,
      // both offset so the first taste of the feed is video.
      const isMillionsSlot = slideIdx >= 10 && (slideIdx - 10) % MILLIONS_INTERLEAVE_EVERY === 0;
      const isMakerSlot = slideIdx >= 4 && (slideIdx - 4) % MAKER_INTERLEAVE_EVERY === 0;
      if (isMillionsSlot) {
        out.push({ kind: "millions", key: `mln-${slideIdx}`, idx: slideIdx });
      } else if (isMakerSlot && makerPool.length > 0) {
        const m = makerPool[makerCursor % makerPool.length];
        out.push({ kind: "maker", key: `mkr-${m.showcase_id}-${slideIdx}`, idx: slideIdx, showcase: m });
        makerCursor += 1;
      } else {
        const v = videos[videoCursor];
        out.push({ kind: "video", key: `${v.video_id}-${slideIdx}`, idx: slideIdx, video: v });
        videoCursor += 1;
      }
      slideIdx += 1;
    }
    return out;
  }, [videos, makerPool]);

  // When tab or channel changes — reset list & reload
  useEffect(() => {
    localStorage.setItem("plyx_feed_tab", tab);
    localStorage.setItem("plyx_feed_channel", channel);
    setVideos([]);
    setCursor(null);
    setActiveIdx(0);
    didFallbackToForYouRef.current = false;  // iter-119 — re-allow fallback for the new tab
    if (containerRef.current) containerRef.current.scrollTop = 0;
    loadMore(null, tab, channel);
  }, [tab, channel, loadMore]);

  // iter-122: bfcache recovery. Navigating back to the feed via the
  // browser back button on iOS/Android restores the page FROZEN from
  // the back-forward cache — any in-flight feed request was killed
  // during navigation and will never resolve, leaving a permanent
  // "Loading Plyx…" spinner. `pageshow` with `persisted=true` is the
  // signal a restore just happened: re-fire the fetch if the feed is
  // stuck loading or empty.
  const loadingRef = useRef(true);
  loadingRef.current = loading;
  const videosLenRef = useRef(0);
  videosLenRef.current = videos.length;
  const videosRef = useRef(videos);
  videosRef.current = videos;
  useEffect(() => {
    const onPageShow = (e) => {
      if (!e.persisted) return;
      if (loadingRef.current || videosLenRef.current === 0) {
        loadMore(null, tab, channel);
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [tab, channel, loadMore]);

  // Always start fresh from slide 0 on mount, even if the browser tried
  // to restore the previous scrollTop. Two RAFs cover the case where the
  // scroll container is laid out after first paint.
  useEffect(() => {
    const reset = () => {
      if (containerRef.current) containerRef.current.scrollTop = 0;
    };
    reset();
    requestAnimationFrame(() => requestAnimationFrame(reset));
  }, []);

  // Plyx creator dopamine toasts — attribution sales, new followers, and
  // view milestones. Polled on mount + every 60s while feed is open.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fmtCount = (n) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(0)}M` :
                          n >= 1_000 ? `${(n/1_000).toFixed(0)}K` : String(n);
    const fetchNotifs = async () => {
      try {
        const { data } = await plyxApi.get("/plyx/attribution/notifications");
        if (cancelled) return;
        const events = [];
        (data.items || []).forEach((c) => {
          const sym = ({ USD: "$", GBP: "£", EUR: "€", CAD: "C$", NGN: "₦" })[c.currency] || `${c.currency} `;
          events.push({
            msg: `+${sym}${c.amount.toFixed(2)} earned · your video drove a sale 🎉`,
          });
        });
        (data.new_followers || []).forEach((f) => {
          events.push({
            msg: `@${f.handle || "someone"} just followed you 👋`,
          });
        });
        (data.milestones || []).forEach((m) => {
          const reward = Number(m.reward_amount) || 0;
          let msg;
          if (m.lifetime_first && reward > 0) {
            msg = `🏆 ${fmtCount(m.threshold)} club unlocked · £${reward.toFixed(2)} earned!`;
          } else if (reward > 0) {
            msg = `🚀 Your video just hit ${fmtCount(m.threshold)} views! · £${reward.toFixed(2)} unlocked`;
          } else {
            msg = `🚀 Your video just hit ${fmtCount(m.threshold)} views!`;
          }
          events.push({ msg });
        });
        events.forEach((e, i) => setTimeout(() => toast.success(e.msg, { duration: 6000 }), i * 1200));
      } catch { /* fire-and-forget */ }
    };
    fetchNotifs();
    const id = setInterval(fetchNotifs, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // iter-119pp: Auto-advance to the next channel when the current
  // channel runs out of content. Instead of leaving the viewer on a
  // static "no more videos" state, we roll them into the next channel
  // in the chip row — same "for you" tab, just a new topic. When we
  // hit the last channel, we wrap back to "All" (empty channel string).
  const advanceChannel = useCallback(() => {
    if (channels.length === 0) return false;
    // Ordered list: ["", channels[0].slug, channels[1].slug, …]
    // "" == the "All" tab.
    const ordered = ["", ...channels.map((c) => c.slug)];
    const currentIdx = ordered.indexOf(channel || "");
    if (currentIdx === -1) return false;
    const nextIdx = (currentIdx + 1) % ordered.length;
    const nextChannel = ordered[nextIdx];
    if (nextChannel === (channel || "")) return false; // only one channel — nowhere to go
    setChannel(nextChannel);
    return true;
  }, [channels, channel]);

  // Stable handler identities so React.memo(VideoCard) can skip
  // re-rendering untouched cards when the feed's state changes —
  // biggest single React-perf win on iOS Safari.
  const handleCommentClick = useCallback((vv) => setCommentVideo(vv), []);
  const handleEmbedRemove = useCallback((vid) => {
    // Strip blocked YouTube videos from the feed in real time. The
    // backend soft-removes them after 3 independent reports, and this
    // local drop means the reporting viewer never sees the
    // "Watch on YouTube" fallback panel — the next clip snaps in.
    // iter-123: removing a card ABOVE the viewport shifts all content
    // up one slide, visually yanking the viewer forward — compensate
    // the scroll position so their current clip stays put. Side
    // effects live OUTSIDE the state updater (StrictMode double-invoke
    // safe).
    const vs = videosRef.current || [];
    const removedIdx = vs.findIndex((v) => v.video_id === vid);
    if (removedIdx === -1) return;
    const el = containerRef.current;
    if (el) {
      const slideH = el.clientHeight || 0;
      const currentSlide = slideH > 0 ? Math.round(el.scrollTop / slideH) : 0;
      if (removedIdx < currentSlide && slideH > 0) {
        requestAnimationFrame(() => {
          try { el.scrollTop = Math.max(0, el.scrollTop - slideH); } catch { /* ignore */ }
        });
      }
    }
    setVideos((cur) => cur.filter((v) => v.video_id !== vid));
  }, []);

  // Detect which card is in view via IntersectionObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.7) {
            const idx = Number(entry.target.getAttribute("data-idx"));
            if (!Number.isNaN(idx)) setActiveIdx(idx);
          }
        }
        // Prefetch when nearing end
        const lastEntry = entries[entries.length - 1];
        if (lastEntry?.isIntersecting) {
          const lastIdx = Number(lastEntry.target.getAttribute("data-idx"));
          if (cursor && lastIdx >= slides.length - 3) {
            // More content available — fetch it.
            loadMore(cursor, tab, channel);
          } else if (!cursor && lastIdx >= slides.length - 1 && channel) {
            // iter-119pp: We've exhausted this channel and there's
            // nothing more to fetch. Roll into the next channel
            // automatically so the viewer keeps scrolling without
            // hitting a dead end. Only fires when they've actually
            // reached the last slide — not before.
            advanceChannel();
          }
        }
      },
      { root: el, threshold: [0.7] }
    );
    el.querySelectorAll("[data-plyx-slide]").forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [slides.length, cursor, tab, channel, loadMore, advanceChannel]);

  // iter-120: full-screen loader ONLY on first load / tab swap (empty
  // list). Pagination also flips `loading`, and the old unconditional
  // check unmounted the scroll container mid-swipe — throwing the
  // viewer back to slide 0, which read as "videos repeating" + a long
  // "Loading Plyx…" stall every ~7 swipes.
  if (loading && videos.length === 0) {
    return (
      <div className="fixed inset-0 bg-black text-white flex items-center justify-center" data-testid="plyx-feed-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2"/> Loading Plyx…
      </div>
    );
  }

  if (videos.length === 0) {
    // iter-122: network failure ≠ empty catalogue. Show a clear retry
    // screen when the initial fetch failed instead of telling the user
    // "no videos yet".
    if (loadError) {
      return (
        <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center gap-4 px-6" data-testid="plyx-feed-error">
          <div className="text-lg font-bold">Couldn't load the feed</div>
          <p className="text-sm text-white/60 text-center max-w-[280px]">
            Check your connection — then tap below and we'll pull the latest clips.
          </p>
          <button
            type="button"
            onClick={() => loadMore(null, tab, channel)}
            data-testid="plyx-feed-retry-btn"
            className="px-6 py-2.5 rounded-full font-semibold text-[#0A0A0A] bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]"
          >
            Tap to retry
          </button>
          <span className="text-[10px] text-white/30 mt-2" data-testid="plyx-build-stamp">build {PLYX_BUILD}</span>
        </div>
      );
    }
    return <EmptyFeed user={user}/>;
  }

  return (
    <div
      className="fixed inset-0 bg-black"
      data-testid="plyx-feed"
      style={{ height: "100dvh" }}
    >
      <StandaloneExitChip />
      {/* iter-119qq: Removed FeedSpotlightChip, PlyxNewsTicker,
          PlyxTagline, and PlyxMovingTagline. User feedback: the feed
          had too many clusters on the top/center and the moving
          "moments become movements" tagline was distracting. Now the
          feed shows just the video, tabs, channel chips, and the
          right-rail actions — nothing else fighting for attention. */}
      <div
        ref={containerRef}
        data-testid="plyx-feed-scroll"
        className="h-full overflow-y-auto snap-y snap-mandatory scrollbar-none"
        style={{ scrollSnapType: "y mandatory", WebkitOverflowScrolling: "touch", overscrollBehaviorY: "contain" }}
      >
        {slides.map((s) => {
          // Feed virtualization — asymmetric window [active-1 … active+2]:
          // the card AFTER next mounts while the viewer is idle-watching,
          // so a swipe never pays the mount cost mid-scroll (that mount
          // jank was the "small pause between videos" reviewers felt).
          const isNear = s.idx >= activeIdx - 1 && s.idx <= activeIdx + 2;
          return (
          <div
            key={s.key}
            data-plyx-slide
            data-idx={s.idx}
            className="h-full w-full snap-start snap-always"
            style={{ contain: "layout style paint" }}
          >
            {!isNear ? (
              <div className="h-full w-full bg-black relative">
                {s.kind === "video" && s.video.thumbnail_url && (
                  <img
                    src={plyxMediaUrl(s.video.thumbnail_url)}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover opacity-70"
                  />
                )}
              </div>
            ) : s.kind === "millions" ? (
              <MillionsFeedCard />
            ) : s.kind === "maker" ? (
              <MakerFeedCard showcase={s.showcase} />
            ) : (
              <VideoCard
                video={s.video}
                isActive={s.idx === activeIdx}
                isNext={s.idx === activeIdx + 1}
                isPrev={s.idx === activeIdx - 1}
                onCommentClick={handleCommentClick}
                onEmbedRemove={handleEmbedRemove}
              />
            )}
          </div>
          );
        })}
        {cursor && (
          <div className="h-full w-full flex items-center justify-center text-white/60 snap-start" data-testid="plyx-feed-loading-more">
            <Loader2 className="w-5 h-5 animate-spin"/>
          </div>
        )}
      </div>

      {/* iter-119uu: Removed the top-right Plyx logo pill.
          Feedback: user asked to remove it because it added visual
          weight to the top-right corner. Brand identity is still
          preserved via the persistent Plyx emblem watermark that
          sits on every VideoCard (top-right of each video, iter-90). */}
      <div
        className="absolute right-3 z-50 flex items-center gap-2"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 0.875rem)" }}
      >
        {/* Feed tabs — desktop-only inline, mobile version lives on
            row 2 below to avoid colliding with Log in / Sign up on
            narrow phones. */}
        <div
          className="hidden sm:flex items-center gap-1.5"
          data-testid="plyx-feed-tabs-desktop"
        >
          {[
            { k: "foryou", label: "For You" },
            { k: "following", label: "Following" },
            { k: "new", label: "New" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`plyx-tab-${t.k}-desktop`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                tab === t.k ? "bg-white text-[#0A0A0A]" : "text-white/90 hover:text-white bg-black/60 backdrop-blur-md border border-white/10"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile-only feed tabs — second row, left side, smaller pills.
          Keeps the topbar uncluttered while still letting phone users
          flip between For You / Following / New. */}
      <div
        className="sm:hidden absolute left-3 z-40 flex items-center gap-1.5"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 3.75rem)" }}
        data-testid="plyx-feed-tabs"
      >
        {[
          { k: "foryou", label: "For You" },
          { k: "following", label: "Following" },
          { k: "new", label: "New" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            data-testid={`plyx-tab-${t.k}`}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
              tab === t.k ? "bg-white text-[#0A0A0A]" : "text-white/90 bg-black/60 backdrop-blur-md border border-white/10"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Channel chip row — editorial topic filters for current
          affairs (Football, World Cup, News, Politics, Music, etc.).
          Sits below the brand strip on the left so it doesn't fight
          the topbar action buttons on the right. Horizontally
          scrollable on narrow screens. Empty channels are filtered
          out server-side. */}
      {channels.length > 0 && (
        <div
          data-testid="plyx-feed-channels"
          className={`absolute left-3 right-3 sm:right-auto sm:max-w-[60%] z-40 flex items-center gap-1 overflow-x-auto pb-0.5 scrollbar-hide transition-opacity duration-300 ${
            activeIdx === 0 ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          style={{ top: "calc(env(safe-area-inset-top, 0px) + 6.75rem)" }}
        >
          <button
            type="button"
            onClick={() => setChannel("")}
            data-testid="plyx-channel-all"
            className={`shrink-0 px-2 py-[3px] rounded-full text-[10px] font-semibold transition-all ${
              channel === ""
                ? "bg-white text-[#0A0A0A]"
                : "bg-black/60 backdrop-blur-md text-white/90 border border-white/10 hover:text-white"
            }`}
          >
            All
          </button>
          {channels.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => setChannel(c.slug)}
              data-testid={`plyx-channel-${c.slug}`}
              title={`${c.count} clip${c.count === 1 ? "" : "s"}`}
              className={`shrink-0 px-2 py-[3px] rounded-full text-[10px] font-semibold transition-all ${
                channel === c.slug
                  ? "bg-gradient-to-r from-[#FF2D7A] to-[#FFB42D] text-[#0A0A0A]"
                  : "bg-black/60 backdrop-blur-md text-white/90 border border-white/10 hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      {/* User controls — moved to TOP-LEFT (replacing the old logo spot)
          so the brand mark gets the prime right-corner slot. Coins +
          Pro + Live + Upload + Sign-in CTAs all live here. */}
      <div
        className="absolute left-3 z-50 flex items-center gap-2"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 0.875rem)" }}
      >
        {user ? (
          <>
            <Link
              to="/plyx/wallet"
              data-testid="plyx-nav-coins"
              title={`${(user.coin_balance ?? 0).toLocaleString()} coins`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-[#FFB42D] to-[#FF2D7A] text-[#0A0A0A] hover:opacity-90"
            >
              <Coins className="w-3 h-3" />
              <span className="hidden sm:inline">{(user.coin_balance ?? 0).toLocaleString()}</span>
            </Link>
            <Link
              to="/plyx/pro"
              data-testid="plyx-nav-pro"
              title={user.is_plyx_pro ? "You\u2019re Plyx Pro" : "Upgrade to Plyx Pro"}
              aria-label={user.is_plyx_pro ? "Plyx Pro active" : "Go Pro"}
              className={
                user.is_plyx_pro
                  ? "inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold text-black bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]"
                  : "hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border border-white/15 text-white/80 hover:bg-white/5 transition"
              }
            >
              <Crown className="w-3 h-3" />
              {user.is_plyx_pro ? "PRO" : "Pro"}
            </Link>
            {/* iter-119uu: Discover creators — MADE MORE VISIBLE.
                Previous icon-only version was too subtle on mobile;
                user reported they couldn't find it. Now a filled
                white/10 pill with the "Discover" label always
                visible so it reads as a clear action, not chrome. */}
            <Link
              to="/plyx/discover"
              data-testid="plyx-nav-discover"
              title="Discover people to follow"
              aria-label="Discover creators"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white bg-black/60 backdrop-blur-md border border-white/30 hover:bg-black/80 transition-colors"
            >
              <Compass className="w-3.5 h-3.5" />
              <span>Discover</span>
            </Link>
            <Link
              to={goldenActive ? "/plyx/millions" : "/plyx/games"}
              data-testid="plyx-feed-millions-btn"
              title={goldenActive ? "🌟 Golden Hour is LIVE — 2× points, 1,000-coin pot!" : "Plyx Games — Millions, Brain Clash live rooms & more"}
              aria-label="Play Plyx games"
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold text-black bg-gradient-to-r from-[#FFB42D] to-[#FF8A2D] shadow-[0_4px_12px_-4px_rgba(255,180,45,0.6)] hover:opacity-90 transition-opacity ${goldenActive ? "animate-pulse ring-2 ring-[#FFB42D]/70" : ""}`}
            >
              <span aria-hidden="true">{goldenActive ? "🌟" : "🎮"}</span>
              <span>{goldenActive ? "Golden Hour" : "Games"}</span>
            </Link>
            <Link
              to="/plyx/live/new"
              data-testid="plyx-feed-go-live-btn"
              title="Start a live broadcast"
              aria-label="Go live"
              className="relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold text-xs border border-[#FF2D7A]/70 text-white bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/15 hover:bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/30 transition-colors"
            >
              <span className="relative flex items-center justify-center w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] opacity-75 animate-ping"/>
                <span className="relative w-2 h-2 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]"/>
              </span>
              <span className="hidden sm:inline">Go Live</span>
            </Link>
            <Link
              to="/plyx/upload"
              data-testid="plyx-feed-upload-btn"
              aria-label="Upload"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold text-white text-xs"
              style={{ background: "#E85D4E" }}
            >
              <Plus className="w-3.5 h-3.5"/>
              <span className="hidden sm:inline">Upload</span>
            </Link>
            <Link
              to={`/plyx/u/${user.handle}`}
              data-testid="plyx-feed-profile-btn"
              aria-label="My videos"
              title="My videos"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 border border-white/15 text-white hover:bg-white/20 transition"
            >
              {user.avatar_url
                ? <img src={user.avatar_url} alt={user.handle} className="w-full h-full rounded-full object-cover"/>
                : <User className="w-4 h-4"/>}
            </Link>
          </>
        ) : (
          <div className="flex items-center gap-2" data-testid="plyx-feed-guest-auth">
            <Link
              to="/plyx/discover"
              data-testid="plyx-feed-guest-discover"
              title="Discover people to follow"
              aria-label="Discover creators"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white bg-black/60 backdrop-blur-md border border-white/30 hover:bg-black/80 transition-colors"
            >
              <Compass className="w-3.5 h-3.5" />
              <span>Discover</span>
            </Link>
            <Link
              to={goldenActive ? "/plyx/millions" : "/plyx/games"}
              data-testid="plyx-feed-guest-millions"
              title={goldenActive ? "🌟 Golden Hour is LIVE — 2× points, 1,000-coin pot!" : "Plyx Games — Millions, Brain Clash live rooms & more"}
              aria-label="Play Plyx games"
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-extrabold text-black bg-gradient-to-r from-[#FFB42D] to-[#FF8A2D] shadow-[0_4px_12px_-4px_rgba(255,180,45,0.6)] hover:opacity-90 transition-opacity ${goldenActive ? "animate-pulse ring-2 ring-[#FFB42D]/70" : ""}`}
            >
              <span aria-hidden="true">{goldenActive ? "🌟" : "🎮"}</span>
              <span>{goldenActive ? "Golden Hour" : "Games"}</span>
            </Link>
            <Link
              to="/plyx/login"
              data-testid="plyx-feed-login-btn"
              className="inline-flex items-center px-4 py-1.5 rounded-full font-bold text-sm text-white bg-black/70 backdrop-blur-md border border-white/40 shadow-lg hover:bg-black/90"
            >
              Log in
            </Link>
            <Link
              to="/plyx/register"
              data-testid="plyx-feed-signup-btn"
              className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full font-semibold text-white text-sm bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] hover:bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] transition-colors shadow-[0_4px_14px_-4px_rgba(232,93,78,0.5)]"
            >
              <Sparkles className="w-3.5 h-3.5"/> Join Plyx
            </Link>
          </div>
        )}
      </div>

      {/* iter-119ss: Removed the bottom-left "About Plyx" link,
          the bottom-right "Leaderboard" link, and the huge
          floating "Go Live" pink FAB. All three added visual
          clutter that competed with the video content for the
          viewer's attention — user feedback: "let it look more
          clean like Instagram". Access is preserved: About lives
          on the /plyx/about page (linked from the empty state and
          the footer), Leaderboard is a link inside the profile
          menu, and Go Live is already available as the pink pill
          in the top-left user controls row. */}

      {/* iter-119qq: Removed the duplicate "Join Plyx · free" bottom-
          right FAB. The top-right pill already offers Join Plyx +
          Login for guest viewers — the second bottom-right cluster
          was adding to the "too many things on top and center" noise
          the user complained about. Guest CTA discoverability is
          preserved via the top-right pill and the Join Plyx pill in
          the top-of-feed row. */}

      {/* Comment drawer */}
      {commentVideo && (
        <CommentDrawer
          video={commentVideo}
          onClose={() => setCommentVideo(null)}
          onCommentPosted={(videoId) => {
            setVideos((vs) => vs.map((v) => v.video_id === videoId ? { ...v, comment_count: (v.comment_count || 0) + 1 } : v));
          }}
        />
      )}

      {/* Daily login bonus — floating claim card, once per day */}
      <DailyBonusCard />

      {/* iter-119ss: Removed the floating PlyxShareFab. It was
          another floating element on the feed adding to the
          clutter. The Share button is still available on every
          VideoCard's right-rail (Share icon) so viral reach is
          preserved without cluttering the feed chrome. */}
    </div>
  );
};

const EmptyFeed = ({ user }) => {
  // AI Discover Creators strip (iter-115). When the feed has nothing
  // to show, pull stat-driven suggestions from /plyx/ai/discover-creators
  // so the viewer always has SOMEONE to follow. Free (no LLM credit).
  const [suggested, setSuggested] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await plyxApi.get("/plyx/ai/discover-creators?limit=6");
        if (alive) setSuggested(r.data?.suggestions || []);
      } catch { /* silent — empty state is fine */ }
    })();
    return () => { alive = false; };
  }, []);
  return (
  <div className="fixed inset-0 bg-[#0A0A0A] text-white flex items-center justify-center px-6 overflow-y-auto" data-testid="plyx-feed-empty">
    <StandaloneExitChip />
    <div className="max-w-md text-center">
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" className="mx-auto mb-6">
        <defs>
          <linearGradient id="emptyGrad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FF2D7A"/>
            <stop offset="0.5" stopColor="#FFB42D"/>
            <stop offset="1" stopColor="#00E0C7"/>
          </linearGradient>
        </defs>
        <polygon points="14,10 14,54 52,32" fill="url(#emptyGrad)"/>
      </svg>
      <h1 className="font-display text-4xl mb-3">Plyx is just getting started</h1>
      <p className="text-white/70 mb-8">No videos yet. Be one of the first to post — moments become movements here.</p>
      {user ? (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/plyx/upload"
            data-testid="plyx-empty-upload-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-[#0A0A0A]"
            style={{ background: "#E85D4E" }}
          >
            <Plus className="w-5 h-5"/> Upload the first video
          </Link>
          <Link
            to="/plyx/live/new"
            data-testid="plyx-empty-go-live-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold border border-[#FF2D7A]/70 text-white bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/15 hover:bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/30"
          >
            <span className="relative flex items-center justify-center w-2.5 h-2.5">
              <span className="absolute inset-0 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] opacity-75 animate-ping"/>
              <span className="relative w-2.5 h-2.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]"/>
            </span>
            Go Live now
          </Link>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/plyx/register"
            data-testid="plyx-empty-register-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-[#0A0A0A]"
            style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
          >
            Join Plyx
          </Link>
          <Link
            to="/plyx/login"
            data-testid="plyx-empty-login-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:bg-white/10 text-sm font-semibold"
          >
            I already have a Plyx account
          </Link>
        </div>
      )}
      <Link to="/plyx/about" className="block mt-8 text-xs text-white/40 hover:text-white/80" data-testid="plyx-empty-about-link">
        Learn about Plyx →
      </Link>

      {/* AI Discover Creators strip — always shown last on empty feeds
          so viewers leave with someone to follow. Stat-driven, free. */}
      {suggested.length > 0 && (
        <div className="mt-10 text-left" data-testid="plyx-empty-discover-strip">
          <div className="flex items-center gap-2 mb-3 justify-center">
            <Sparkles className="w-4 h-4 text-[#FFB42D]" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold">
              You might like
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {suggested.slice(0, 6).map((s) => (
              <Link
                key={s.creator_id}
                to={`/plyx/u/${encodeURIComponent(s.handle || s.creator_id)}`}
                data-testid={`plyx-empty-discover-creator-${s.creator_id}`}
                className="flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] flex items-center justify-center text-[#0A0A0A] font-black text-xs shrink-0">
                  {(s.handle || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold truncate">@{s.handle || "creator"}</div>
                  <div className="text-[10px] text-white/50 truncate">{s.reason}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

export default PlyxFeed;
